import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import {
  Radio,
  Cpu,
  Globe,
  ArrowRight,
  Shield,
  Sparkles,
  Plus,
  Trash2,
  Bookmark,
  Send,
  ExternalLink,
  RefreshCw,
  SlidersHorizontal,
  Check,
  AlertTriangle,
  FileText,
  Briefcase,
  CheckCircle,
  Info,
  Layers,
  HelpCircle
} from 'lucide-react';

interface TechUpdate {
  id: string;
  title: string;
  sourceName: string;
  sourceUrl: string;
  sourceType: string;
  category: string;
  provider: string;
  summary: string;
  relevanceForNexoraX: string;
  impactLevel: string;
  releaseDate: string;
  detectedDate: string;
  tags: string[];
  status: 'Draft' | 'Published' | 'Needs Review' | 'Watchlist' | 'Archived';
  manualReviewStatus: 'Unreviewed' | 'Reviewed' | 'Verified Source' | 'Official Source' | 'Needs Fact Check' | 'Do Not Publish';
  saved?: boolean;
  tracked?: boolean;
  createdAt: string;
  updatedAt: string;
}

interface SourceWatchlistItem {
  id: string;
  name: string;
  url: string;
  category: 'Official AI Sources' | 'Automation Sources' | 'Developer Sources' | 'Smart Tech Sources';
  status: 'Manual tracking' | 'Official feed connected' | 'API connected server-side' | 'Needs review' | 'Disabled';
  lastCheckedDate: string;
}

interface NexusTechUpdateRadarProps {
  isAdmin?: boolean;
  onAddLog: (module: string, action: string, status: 'SUCCESS' | 'WARNING' | 'DENIED' | 'PENDING_APPROVAL', details: string) => void;
  onAddProductIdea?: (idea: any) => void; // Link to gadget discovery
  onAddWorkSuiteTask?: (task: any) => void; // Link to worksuite
}

const UPDATE_CATEGORIES = [
  'ALL',
  'AI Models',
  'Gemini / AI Studio',
  'ChatGPT / OpenAI',
  'Claude',
  'Mistral',
  'Llama',
  'Hermes / Open Models',
  'AI Agents',
  'AI Automation',
  'No-Code Automation',
  'Developer Tools',
  'App Builders',
  'AI Video',
  'AI Voice',
  'AI Image Tools',
  'Smart Gadgets',
  'Robotics',
  'Smart Home Tech',
  'Cloud Platforms',
  'Cybersecurity',
  'Future Technology',
  'Product Launches',
  'Startup Tools'
];

const IMPACT_LEVEL_CATEGORIES = [
  'ALL',
  'Major',
  'Useful',
  'Experimental',
  'Watchlist',
  'Future Potential'
];

const SOURCE_TYPES = [
  'Official blog',
  'Release notes',
  'Product update',
  'YouTube review',
  'Tech article',
  'Developer documentation',
  'Research paper',
  'Community discussion',
  'Manual admin note'
];

export const NexusTechUpdateRadar: React.FC<NexusTechUpdateRadarProps> = ({
  isAdmin = false,
  onAddLog,
  onAddProductIdea,
  onAddWorkSuiteTask
}) => {
  const [updates, setUpdates] = useState<TechUpdate[]>([]);
  const [watchlist, setWatchlist] = useState<SourceWatchlistItem[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Public Filters
  const [selectedCategory, setSelectedCategory] = useState('ALL');
  const [selectedImpact, setSelectedImpact] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'latest' | 'impact' | 'name'>('latest');

  // Subscription State
  const [subscribedEmail, setSubscribedEmail] = useState('');
  const [consentChecked, setConsentChecked] = useState(false);
  const [subMessage, setSubMessage] = useState('');

  // Agent Dispatch Modal
  const [activeAgentUpdate, setActiveAgentUpdate] = useState<TechUpdate | null>(null);
  const [agentInstruction, setAgentInstruction] = useState('');
  const [agentResponse, setAgentResponse] = useState('');
  const [agentLoading, setAgentLoading] = useState(false);

  // Admin Form state
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingUpdate, setEditingUpdate] = useState<Partial<TechUpdate> | null>(null);
  const [scoutProgressMsg, setScoutProgressMsg] = useState('');
  const [scoutTopicInput, setScoutTopicInput] = useState('');
  const [scoutingActive, setScoutingActive] = useState(false);

  // Watchlist source form
  const [isSourceFormOpen, setIsSourceFormOpen] = useState(false);
  const [editingSource, setEditingSource] = useState<Partial<SourceWatchlistItem> | null>(null);

  // Load content
  const fetchUpdates = async () => {
    try {
      setLoading(true);
      const url = isAdmin ? '/api/admin/marketplace/tech-updates' : '/api/marketplace/tech-updates';
      const response = await fetch(url);
      const data = await response.json();
      if (data.success) {
        setUpdates(data.updates || []);
      }
    } catch (e: any) {
      console.error("Failed fetching updates:", e);
    } finally {
      setLoading(false);
    }
  };

  const fetchWatchlist = async () => {
    try {
      if (!isAdmin) return;
      const response = await fetch('/api/admin/marketplace/tech-source-watchlist');
      const data = await response.json();
      if (data.success) {
        setWatchlist(data.watchlist || []);
      }
    } catch (e: any) {
      console.error("Failed fetching watchlist:", e);
    }
  };

  useEffect(() => {
    fetchUpdates();
    if (isAdmin) {
      fetchWatchlist();
    }
  }, [isAdmin]);

  // Public Actions
  const handleToggleSave = async (item: TechUpdate) => {
    try {
      // Opt-in UI change
      setUpdates(prev => prev.map(u => u.id === item.id ? { ...u, saved: !u.saved } : u));
      const response = await fetch(`/api/marketplace/tech-updates/${item.id}/save`, { method: 'POST' });
      const data = await response.json();
      
      const nextSavedState = data.saved !== undefined ? data.saved : !item.saved;
      onAddLog(
        'Tech Update Radar',
        `${nextSavedState ? 'Saved' : 'Unsaved'} item: "${item.title}"`,
        'SUCCESS',
        `User toggled personal tracking preference for update ID: ${item.id}.`
      );
    } catch (e) {
      console.error(e);
    }
  };

  const handleToggleTrack = async (item: TechUpdate) => {
    try {
      setUpdates(prev => prev.map(u => u.id === item.id ? { ...u, tracked: !u.tracked } : u));
      await fetch(`/api/marketplace/tech-updates/${item.id}/track-topic`, { method: 'POST' });
      onAddLog(
        'Tech Update Radar',
        `Tracked topic: "${item.title}"`,
        'SUCCESS',
        `User opted into instant email triggers for tags: ${item.tags.join(', ')}.`
      );
    } catch (e) {
      console.error(e);
    }
  };

  const handleSubscribe = (e: React.FormEvent) => {
    e.preventDefault();
    if (!consentChecked) {
      setSubMessage('Please agree to follow verified source rules.');
      return;
    }
    if (!subscribedEmail.trim() || !subscribedEmail.includes('@')) {
      setSubMessage('Invalid system routing email.');
      return;
    }
    onAddLog(
      'Tech Update Radar',
      `User Subscription: "${subscribedEmail}"`,
      'SUCCESS',
      `Subscribed to the weekly Nexus newsletter containing official summaries & direct-billing updates.`
    );
    setSubMessage('ACCESS GRANTED! Welcome to the weekly tech briefing feed.');
    setSubscribedEmail('');
  };

  // Agent Dispatch Action
  const dispatchAgentAnalysis = async () => {
    if (!activeAgentUpdate) return;
    try {
      setAgentLoading(true);
      setAgentResponse('');
      const response = await fetch(`/api/marketplace/tech-updates/${activeAgentUpdate.id}/send-to-agent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ instruction: agentInstruction })
      });
      const data = await response.json();
      if (data.success) {
        setAgentResponse(data.result);
        onAddLog(
          'Tech Scout Agent',
          `Scout dispatch: "${activeAgentUpdate.title}"`,
          'SUCCESS',
          `Delivered advisory brief to remote nodes successfully.`
        );
      }
    } catch (e: any) {
      console.error(e);
      setAgentResponse('COMMUNICATION DISRUPTED: Could not connect to Task Node.');
    } finally {
      setAgentLoading(false);
    }
  };

  // Admin Actions
  const handleSaveUpdateRecord = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingUpdate) return;
    try {
      const isNew = !editingUpdate.id;
      const response = await fetch('/api/admin/marketplace/tech-updates', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editingUpdate)
      });
      const data = await response.json();
      if (data.success) {
        onAddLog(
          'Tech Update Manager',
          `${isNew ? 'Added' : 'Updated'} update: "${editingUpdate.title}"`,
          'SUCCESS',
          `Database sync completed for ID ${data.update.id}. Status changed to ${editingUpdate.status}.`
        );
        setIsEditorOpen(false);
        setEditingUpdate(null);
        fetchUpdates();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleUpdateStatus = async (item: TechUpdate, nextStatus: TechUpdate['status']) => {
    try {
      const response = await fetch(`/api/admin/marketplace/tech-updates/${item.id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: nextStatus })
      });
      const data = await response.json();
      if (data.success) {
        onAddLog('Tech Update Manager', `Status updated: "${item.title}"`, 'SUCCESS', `Shifted publication state safely to [${nextStatus}].`);
        fetchUpdates();
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleDeleteUpdate = async (id: string, title: string) => {
    if (!window.confirm(`Are you sure you want to permanently delete tech update:\n"${title}"?`)) return;
    try {
      const response = await fetch(`/api/admin/marketplace/tech-updates/${id}`, { method: 'DELETE' });
      const data = await response.json();
      if (data.success) {
        onAddLog('Tech Update Manager', `Removed item: "${title}"`, 'SUCCESS', `Purged document ID ${id} from local JSON storage.`);
        fetchUpdates();
      }
    } catch (err) {
      console.error(err);
    }
  };

  // Scout Agent Trigger
  const triggerScoutAgent = async () => {
    if (!scoutTopicInput.trim()) return;
    try {
      setScoutingActive(true);
      setScoutProgressMsg('Scouting NexoraX Digital, Anthropic Claude, and open model changelogs...');
      const response = await fetch('/api/admin/agents/tech-scout/run', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ topic: scoutTopicInput })
      });
      const data = await response.json();
      if (data.success) {
        setScoutProgressMsg(`Successfully compiled draft! ID: ${data.draft.id}. Title: "${data.draft.title}". Check your drafts list.`);
        onAddLog(
          'Tech Scout Agent',
          `Automated Discovery Draft: "${data.draft.title}"`,
          'SUCCESS',
          `Scout node generated draft for category "${data.draft.category}" requiring manual review.`
        );
        fetchUpdates();
      }
    } catch (e) {
      console.error(e);
      setScoutProgressMsg('Scouting failed due to security-rule block or rate limit.');
    } finally {
      setScoutingActive(false);
    }
  };

  // Sourcing & product additions
  const handlePushToMarketplace = async (item: TechUpdate) => {
    try {
      const g = {
        title: item.title,
        category: item.category.includes('Gadg') ? item.category : 'AI Gadgets',
        shortDescription: item.summary,
        priceText: '£99.00',
        productImage: 'https://images.unsplash.com/photo-1546776310-eef45dd6d63c?w=600',
        availabilityText: 'Coming Soon',
        tags: [...item.tags, 'Scouted', 'Future Tech'],
        priority: 4,
        wholesalePotential: 'High',
        sourceVideoId: '',
        sourceVideoUrl: item.sourceUrl,
        sourceChannelName: item.sourceName,
        sourceChannelUrl: '',
        disclosureLabel: 'Tech Update Scouted Item',
        productStatus: 'Draft',
        futureStatus: 'Concept Product',
        supplierStatus: 'Supplier Search Needed'
      };

      const response = await fetch('/api/admin/marketplace/future-gadgets', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(g)
      });
      const data = await response.json();
      if (data.success) {
        onAddLog(
          'Tech Update Manager',
          `Deployed concept to Future Gadgets Matrix: "${item.title}"`,
          'SUCCESS',
          `Successfully registered product draft inside global JSON catalog. ID assigned: ${data.gadget.id}`
        );
        alert(`SUCCESS!\n\n"${item.title}" has been registered as an active Draft within your Future Smart Gadgets Discovery Marketplace.`);
      }
    } catch (e: any) {
      console.error(e);
      alert('Could not push to the smart tech database.');
    }
  };

  const handlePushToWorkSuite = (item: TechUpdate) => {
    if (onAddWorkSuiteTask) {
      onAddWorkSuiteTask({
        title: `Integrate / Assess: ${item.title}`,
        project: 'AI Platforms Ops',
        priority: item.impactLevel === 'Major' ? 'URGENT' : 'HIGH',
        status: 'TODO'
      });
      onAddLog(
        'WorkSuite Sync',
        `Created update assessment task: "${item.title}"`,
        'SUCCESS',
        `WorkSuite task initialized linking to source documentation ${item.sourceUrl}.`
      );
      alert(`SUCCESS!\n\nWorkSuite review task deployed for: "${item.title}"`);
    } else {
      alert(`WorkSuite sync connection is active. Directing pipeline to Secure Register Workspace.`);
    }
  };

  // Watchlist source editor save
  const handleSaveSource = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingSource) return;
    try {
      const response = await fetch('/api/admin/marketplace/tech-source-watchlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editingSource)
      });
      const data = await response.json();
      if (data.success) {
        onAddLog('Tech Update Watchlist', `Configured channel: "${editingSource.name}"`, 'SUCCESS', 'Watchlist parameters updated.');
        setIsSourceFormOpen(false);
        setEditingSource(null);
        fetchWatchlist();
      }
    } catch (e) {
      console.error(e);
    }
  };

  // Sorting / Filtering Updates
  const filteredUpdates = updates.filter(u => {
    const matchesCategory = selectedCategory === 'ALL' || u.category === selectedCategory || u.provider === selectedCategory;
    const matchesImpact = selectedImpact === 'ALL' || u.impactLevel === selectedImpact;
    const matchesSearch = searchTerm.trim() === '' ||
      u.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.summary.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.provider.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.tags.some(t => t.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesCategory && matchesImpact && matchesSearch;
  });

  const sortedUpdates = [...filteredUpdates].sort((a, b) => {
    if (sortBy === 'latest') {
      return new Date(b.releaseDate || b.createdAt).getTime() - new Date(a.releaseDate || a.createdAt).getTime();
    } else if (sortBy === 'name') {
      return a.title.localeCompare(b.title);
    } else if (sortBy === 'impact') {
      const weights: Record<string, number> = { 'Major': 5, 'Useful': 4, 'Future Potential': 3, 'Watchlist': 2, 'Experimental': 1 };
      return (weights[b.impactLevel] || 0) - (weights[a.impactLevel] || 0);
    }
    return 0;
  });

  return (
    <div className="space-y-8 font-sans">
      
      {/* HEADER SECTION - Obsidian and Glow */}
      <div className="relative overflow-hidden rounded-2xl border border-purple-500/20 bg-zinc-950 p-6 md:p-8 shadow-[0_0_40px_rgba(232,64,243,0.06)]">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-950/20 via-black to-cyan-950/20" />
        {/* Holographic grid indicator */}
        <div className="absolute inset-x-0 bottom-0 h-[1px] bg-gradient-to-r from-transparent via-[#00ffff]/40 to-transparent" />
        
        <div className="relative space-y-4">
          <div className="inline-flex items-center gap-1.5 rounded-full border border-purple-500/30 bg-purple-950/40 px-3 py-1 font-mono text-[9px] font-black uppercase tracking-wider text-pink-400">
            <Radio className="h-3 w-3 animate-pulse text-[#e840f3]" /> Live Updates Hub
          </div>
          
          <div className="space-y-1">
            <h1 className="text-2xl md:text-3.5xl font-sans font-black tracking-tight text-white uppercase">
              Nexus <span className="bg-gradient-to-r from-pink-400 via-purple-400 to-[#00ffff] bg-clip-text text-transparent">Tech Update Radar</span>
            </h1>
            <p className="text-xs font-mono text-[#00ffff] uppercase tracking-wider">
              Track AI, automation, smart tech, model releases, dev tools, and future technology before it goes mainstream.
            </p>
          </div>

          <p className="text-[11.5px] max-w-3xl text-slate-400 font-sans leading-relaxed">
            Nexus Tech Update Radar helps users follow the latest updates across AI tools, automation platforms, smart gadgets, developer systems, creative AI, AI agents, and future technology releases — all organised into one premium marketplace-style dashboard.
          </p>

          <div className="flex flex-wrap gap-4 pt-1 text-[9.5px] font-mono text-slate-500 uppercase">
            <div>TOTAL MONITORED NODES: <span className="font-extrabold text-[#e840f3]">23 Channels</span></div>
            <div className="hidden sm:block">•</div>
            <div>STATUS: <span className="font-extrabold text-emerald-400">Public Preview Ready</span></div>
            <div className="hidden sm:block">•</div>
            <div>LAST AUTO-SWEEP: <span className="text-white">Active</span></div>
          </div>
        </div>
      </div>

      {/* DUAL MODE TABS (PUBLIC VS ADMIN TECH MANAGER) */}
      {isAdmin && (
        <div className="flex border-b border-zinc-900 pb-3 gap-2 font-mono text-[10px] font-black uppercase tracking-widest">
          <div className="text-slate-500 mr-auto self-center select-none">ADMINISTRATIVE PANEL OVERLAYS:</div>
          <button
            onClick={() => setIsEditorOpen(true)}
            className="py-2 px-4 bg-purple-950/40 text-pink-400 border border-purple-500/35 rounded-xl hover:bg-purple-950/70 transition-all cursor-pointer"
          >
            Deploy Manual Feed Update
          </button>
          <button
            onClick={() => {
              setEditingSource({});
              setIsSourceFormOpen(true);
            }}
            className="py-2 px-4 bg-zinc-900 text-slate-400 border border-zinc-800 rounded-xl hover:text-white transition-all cursor-pointer"
          >
            Add Watchlist Channel
          </button>
        </div>
      )}

      {/* CORE FEATURED PLATFORM TRACKER GROUPS */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Track Category 1 */}
        <div className="rounded-xl border border-zinc-900 bg-zinc-950/60 p-4 font-mono text-[10.5px]">
          <div className="flex justify-between items-center border-b border-zinc-900 pb-2 mb-3">
            <span className="font-black text-[#e840f3] uppercase flex items-center gap-1.5"><Cpu size={14} /> Gemini / AI Studio Tracker</span>
            <span className="text-[8px] bg-emerald-950 text-emerald-400 px-1 border border-emerald-900 rounded">ACTIVE</span>
          </div>
          <ul className="space-y-1.5 text-slate-400 text-[10px]">
            <li className="flex justify-between"><span className="normal-case">Gemini 1.5 &amp; 2.5 API Nodes</span> <span className="text-[#00ffff]">Official feed connected</span></li>
            <li className="flex justify-between"><span className="normal-case">AI Studio Playground features</span> <span className="text-amber-500">Manual tracking</span></li>
            <li className="flex justify-between"><span className="normal-case">Live Audio Stream Models</span> <span className="text-[#00ffff]">Official feed connected</span></li>
            <li className="flex justify-between"><span className="normal-case">Workspace Add-on Extensions</span> <span className="text-slate-600">Manual tracking</span></li>
          </ul>
        </div>

        {/* Track Category 2 */}
        <div className="rounded-xl border border-zinc-900 bg-zinc-950/60 p-4 font-mono text-[10.5px]">
          <div className="flex justify-between items-center border-b border-zinc-900 pb-2 mb-3">
            <span className="font-black text-pink-400 uppercase flex items-center gap-1.5"><Shield size={14} /> OpenAI &amp; Claude Tracker</span>
            <span className="text-[8px] bg-zinc-900 text-[#00ffff] px-1 border border-zinc-850 rounded">AUTO-PIPELINE</span>
          </div>
          <ul className="space-y-1.5 text-slate-400 text-[10px]">
            <li className="flex justify-between"><span className="normal-case">Claude Artifacts &amp; Developer Tooling</span> <span className="text-[#00ffff]">API connected</span></li>
            <li className="flex justify-between"><span className="normal-case">ChatGPT Realtime Speech Audio API</span> <span className="text-[#00ffff]">API connected</span></li>
            <li className="flex justify-between"><span className="normal-case">Anthropic Multi-Agent SDK</span> <span className="text-amber-500">Manual tracking</span></li>
            <li className="flex justify-between"><span className="normal-case">Llama &amp; Open Models Weights</span> <span className="text-emerald-400">Official feed</span></li>
          </ul>
        </div>

        {/* Track Category 3 */}
        <div className="rounded-xl border border-zinc-900 bg-zinc-950/60 p-4 font-mono text-[10.5px] md:col-span-2 lg:col-span-1">
          <div className="flex justify-between items-center border-b border-zinc-900 pb-2 mb-3">
            <span className="font-black text-[#00ffff] uppercase flex items-center gap-1.5"><Globe size={14} /> Automation &amp; Future Tech</span>
            <span className="text-[8px] bg-amber-950 text-amber-400 px-1 border border-amber-900 rounded">SWEEPING</span>
          </div>
          <ul className="space-y-1.5 text-slate-400 text-[10px]">
            <li className="flex justify-between"><span className="normal-case">n8n / Make workflow connectors</span> <span className="text-[#00ffff]">Official feed connected</span></li>
            <li className="flex justify-between"><span className="normal-case">Zapier AI Actions registry</span> <span className="text-amber-500">Manual tracking</span></li>
            <li className="flex justify-between"><span className="normal-case">GitHub Changelog &amp; CoPilot features</span> <span className="text-[#00ffff]">Official feed connected</span></li>
            <li className="flex justify-between"><span className="normal-case">Wearables &amp; Supplier launches</span> <span className="text-amber-500">Manual tracking</span></li>
          </ul>
        </div>
      </div>

      {/* TECH SCOUT AUTO-AGENT PIPELINE FOR ADMIN */}
      {isAdmin && (
        <div className="p-5 rounded-2xl border border-pink-500/25 bg-gradient-to-r from-purple-950/10 via-black to-zinc-950 flex flex-col md:flex-row md:items-center justify-between gap-5 shadow-[0_0_20px_rgba(232,64,243,0.03)]">
          <div className="space-y-1.5 max-w-xl">
            <h3 className="font-mono text-xs uppercase font-extrabold text-pink-400 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#e840f3] animate-pulse" /> Nexus Autonomous Tech Scout Agent
            </h3>
            <p className="text-[10px] text-slate-400">
              Agent crawls verified developer feeds, release logs, and hacker boards. Enter a topic keyword below to dispatch the scout agent. All generated outputs are injected as <strong>Drafts requiring Admin approval</strong>.
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-2 shrink-0">
            <input
              type="text"
              placeholder="e.g. Claude 3.7, n8n, Smart Rings..."
              value={scoutTopicInput}
              onChange={(e) => setScoutTopicInput(e.target.value)}
              className="px-3 py-2 bg-black border border-zinc-800 rounded-xl font-mono text-[10px] text-white focus:outline-none focus:border-pink-500 min-w-[200px]"
            />
            <button
              onClick={triggerScoutAgent}
              disabled={scoutingActive || !scoutTopicInput.trim()}
              className="py-2.5 px-4 bg-[#e840f3] hover:bg-[#e840f3]/90 text-black font-mono text-[9px] font-black uppercase tracking-wider rounded-xl transition-all cursor-pointer flex items-center gap-1.5 disabled:opacity-50"
            >
              {scoutingActive ? (
                <>
                  <RefreshCw className="w-3.5 h-3.5 animate-spin" /> Scanning Platform...
                </>
              ) : (
                <>
                  <Send className="w-3.5 h-3.5" /> Dispatch scout Node
                </>
              )}
            </button>
          </div>

          {scoutProgressMsg && (
            <div className="w-full text-[9px] font-mono text-pink-400 bg-pink-950/20 px-3 py-2 border border-pink-900/40 rounded-xl mt-2 select-none">
              {scoutProgressMsg}
            </div>
          )}
        </div>
      )}

      {/* FILTERS & SEARCH ROW */}
      <div className="space-y-4">
        {/* Main top bar filters */}
        <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-4 border-b border-zinc-900 pb-4">
          
          {/* Tag filtering tabs */}
          <div className="flex flex-wrap gap-1.5">
            {UPDATE_CATEGORIES.slice(0, 10).map((cat) => (
              <button
                key={cat}
                onClick={() => setSelectedCategory(cat)}
                className={`py-1.5 px-3 rounded-xl font-mono text-[9px] font-black uppercase tracking-wider transition-all cursor-pointer border ${
                  selectedCategory === cat
                    ? 'bg-[#e840f3]/15 text-[#e840f3] border-[#e840f3]/45 shadow-[0_0_12px_rgba(232,64,243,0.12)]'
                    : 'bg-transparent text-slate-500 border-zinc-900 hover:text-slate-300'
                }`}
              >
                {cat}
              </button>
            ))}
            
            {/* Collapse list dropdown trigger if needed */}
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="py-1 px-2.5 rounded-xl font-mono text-[9px] font-black uppercase tracking-wider bg-black/50 text-slate-400 border border-zinc-800 focus:outline-none focus:border-purple-500"
            >
              <option value="ALL">More Categories...</option>
              {UPDATE_CATEGORIES.slice(10).map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <input
              type="text"
              placeholder="Filter updates, tags, keywords..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-3 py-1.5 bg-black/60 border border-zinc-900 rounded-xl font-mono text-[10px] text-white focus:outline-none focus:border-cyan-500/40 sm:w-[220px]"
            />

            {/* Impact Filter */}
            <select
              value={selectedImpact}
              onChange={(e) => setSelectedImpact(e.target.value)}
              className="py-1 px-3.5 rounded-xl font-mono text-[10px] bg-black/50 text-slate-400 border border-zinc-900 focus:outline-none focus:border-purple-500 uppercase font-bold"
            >
              <option value="ALL">All Impact Levels</option>
              {IMPACT_LEVEL_CATEGORIES.slice(1).map(imp => (
                <option key={imp} value={imp}>{imp} Priority</option>
              ))}
            </select>

            {/* Sort Dropdown */}
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="py-1 px-3.5 rounded-xl font-mono text-[10px] bg-black/50 text-slate-400 border border-zinc-900 focus:outline-none focus:border-purple-500 uppercase font-bold"
            >
              <option value="latest">Latest Swapped</option>
              <option value="name">Alphabetical</option>
              <option value="impact">Highest Priority Impact</option>
            </select>
          </div>
        </div>
      </div>

      {/* CORE CARDS GRID LIST */}
      <div className="space-y-6">
        {loading ? (
          <div className="py-20 text-center font-mono text-[11px] text-slate-500 uppercase flex flex-col items-center justify-center gap-3">
            <RefreshCw className="h-6 w-6 animate-spin text-[#e840f3]" />
            Initializing live radar sweep across developer targets...
          </div>
        ) : sortedUpdates.length === 0 ? (
          <div className="py-20 text-center border border-dashed border-zinc-900 rounded-2xl p-6 font-mono text-[11px] text-slate-500 uppercase">
            No active tech updates match your search filter criteria.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sortedUpdates.map((item) => {
              const isDraft = item.status === 'Draft' || item.status === 'Needs Review';
              return (
                <div
                  key={item.id}
                  className={`relative flex flex-col justify-between rounded-xl overflow-hidden bg-purple-950/5 border hover:shadow-[0_0_20px_rgba(232,64,243,0.06)] transition-all duration-300 group ${
                    isDraft 
                      ? 'border-dashed border-pink-500/30' 
                      : 'border-purple-500/15 hover:border-[#e840f3]/40'
                  }`}
                >
                  {/* Glowing glow header line */}
                  <div className={`absolute top-0 inset-x-0 h-[1.5px] bg-gradient-to-r ${
                    item.impactLevel === 'Major' ? 'from-amber-400 via-pink-500 to-amber-400' : 'from-[#e840f3]/40 to-[#00ffff]/40'
                  }`} />
                  
                  <div className="p-5 space-y-4">
                    {/* Badge header */}
                    <div className="flex justify-between items-start gap-2 text-[8px] font-mono font-black uppercase tracking-widest select-none">
                      <span className="bg-black/90 px-2 py-0.5 border border-zinc-850 rounded text-[#e840f3]">
                        {item.category}
                      </span>
                      
                      <div className="flex gap-1.5 items-center select-none">
                        <span className={`px-2 py-0.5 rounded font-bold ${
                          item.impactLevel === 'Major' ? 'bg-amber-500/10 text-amber-400 border border-amber-500/30' :
                          item.impactLevel === 'Useful' ? 'bg-cyan-950/20 text-[#00ffff] border border-cyan-900/30' :
                          'bg-zinc-950/50 text-slate-500'
                        }`}>
                          {item.impactLevel}
                        </span>

                        {isDraft && (
                          <span className="bg-rose-950/40 text-rose-450 border border-rose-900/30 px-1 rounded animate-pulse">
                            {item.status}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Title */}
                    <div className="space-y-1">
                      <h3 className="text-sm font-sans font-black text-white group-hover:text-[#00ffff] transition-colors leading-snug uppercase tracking-tight">
                        {item.title}
                      </h3>
                      <div className="flex items-center gap-1.5 text-[9px] font-mono text-slate-500">
                        <span className="text-slate-400 font-bold">{item.sourceName}</span>
                        <span>•</span>
                        <span>{item.releaseDate}</span>
                      </div>
                    </div>

                    {/* Summary */}
                    <p className="text-[11px] text-slate-400 font-sans leading-relaxed font-medium">
                      {item.summary}
                    </p>

                    {/* NexoraX relevance note */}
                    <div className="bg-zinc-950/80 p-2.5 rounded-xl border border-zinc-900 text-[10px] font-sans leading-relaxed text-slate-300">
                      <div className="text-[7.5px] font-mono text-[#00ffff] uppercase font-black tracking-wider flex items-center gap-1 mb-1">
                        <Info size={10} className="text-[#00ffff]" /> Strategic Advisory Relevance
                      </div>
                      <p className="normal-case text-slate-450 italic font-medium leading-normal">
                        "{item.relevanceForNexoraX}"
                      </p>
                    </div>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-1">
                      {item.tags.map((tag, tIdx) => (
                        <span
                          key={tIdx}
                          onClick={() => setSearchTerm(tag)}
                          className="bg-black/40 hover:text-[#e840f3] border border-zinc-900/70 px-2 py-0.5 rounded text-[8px] font-mono text-slate-500 uppercase cursor-pointer transition-all"
                        >
                          #{tag}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Actions footer wrapper */}
                  <div className="p-5 border-t border-zinc-950/80 bg-black/30 text-[9px] font-mono uppercase font-black tracking-wider flex flex-col gap-2">
                    {/* Disclosure alert for content safety */}
                    <div className="text-[7.5px] text-zinc-500 leading-normal normal-case border-b border-zinc-950 pb-2 mb-1">
                      Source Review State: <span className="font-bold text-[#00ffff]">{item.manualReviewStatus}</span>. Verified safe summary.
                    </div>

                    {/* Action grid button rows */}
                    <div className="grid grid-cols-2 gap-2">
                      <a
                        href={item.sourceUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="py-2.5 px-3 bg-black hover:bg-zinc-900 border border-zinc-800 rounded-xl text-slate-350 hover:text-white flex items-center justify-center gap-1 text-center cursor-pointer"
                      >
                        Source File <ExternalLink size={10} className="text-slate-500" />
                      </a>
                      
                      <button
                        onClick={() => setActiveAgentUpdate(item)}
                        className="py-2.5 px-3 bg-purple-950/20 text-pink-400 hover:text-white hover:bg-purple-950/40 border border-purple-500/20 rounded-xl flex items-center justify-center gap-1.5 text-center cursor-pointer"
                      >
                        <Send size={10} /> Send to Agent
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => handleToggleSave(item)}
                        className={`py-2.5 px-3 rounded-xl border flex items-center justify-center gap-1 text-center cursor-pointer transition-all ${
                          item.saved
                            ? 'bg-amber-500/10 border-amber-500/40 text-amber-400'
                            : 'bg-black border-zinc-850 hover:border-zinc-700 text-slate-400 hover:text-white'
                        }`}
                      >
                        <Bookmark size={11} className={item.saved ? "fill-amber-400 text-amber-400" : ""} />
                        {item.saved ? 'Saved' : 'Save Update'}
                      </button>

                      <button
                        onClick={() => handleToggleTrack(item)}
                        className={`py-2.5 px-3 rounded-xl border flex items-center justify-center gap-1 text-center cursor-pointer transition-all ${
                          item.tracked
                            ? 'bg-cyan-500/10 border-cyan-500/40 text-cyan-400'
                            : 'bg-black border-zinc-850 hover:border-zinc-700 text-slate-400 hover:text-white'
                        }`}
                      >
                        <CheckCircle size={11} className={item.tracked ? "text-cyan-400" : ""} />
                        {item.tracked ? 'Tracked' : 'Track Topic'}
                      </button>
                    </div>

                    {/* Integrated Platform Actions for Gadget & WorkSuite */}
                    <div className="grid grid-cols-1 gap-1 pt-1 border-t border-zinc-950">
                      <button
                        onClick={() => handlePushToWorkSuite(item)}
                        className="py-1.5 text-slate-500 hover:text-cyan-400 rounded transition-colors text-[7.5px] tracking-wide text-left flex items-center gap-1 cursor-pointer"
                      >
                        <Briefcase size={10} /> Push WorkSuite advisory task
                      </button>
                      
                      {item.category.includes('Smart') || item.tags.some(t => t.toLowerCase().includes('gadget') || t.toLowerCase().includes('home') || t.toLowerCase().includes('wearable')) ? (
                        <button
                          onClick={() => handlePushToMarketplace(item)}
                          className="py-1.5 text-slate-500 hover:text-[#e840f3] rounded transition-colors text-[7.5px] tracking-wide text-left flex items-center gap-1 cursor-pointer"
                        >
                          <Plus className="h-3 w-3 inline text-[#e840f3]" /> Deploy candidate to Future Gadget Marketplace
                        </button>
                      ) : null}
                    </div>

                    {/* Admin Specific Action Controls */}
                    {isAdmin && (
                      <div className="flex gap-1 bg-black/40 p-2 rounded-xl border border-zinc-900 justify-end text-[7.5px] font-bold">
                        <span className="text-slate-500 mr-auto self-center select-none">ADMIN PARMS:</span>
                        <button
                          onClick={() => {
                            setEditingUpdate(item);
                            setIsEditorOpen(true);
                          }}
                          className="px-2 py-0.5 bg-purple-950/50 text-[#e840f3] border border-purple-950/40 rounded hover:bg-purple-950/80"
                        >
                          Edit
                        </button>
                        
                        {item.status === 'Draft' ? (
                          <button
                            onClick={() => handleUpdateStatus(item, 'Published')}
                            className="px-2 py-0.5 bg-emerald-950/40 text-[#00ffff] border border-emerald-900/30 rounded"
                          >
                            Approve &amp; Publish
                          </button>
                        ) : (
                          <button
                            onClick={() => handleUpdateStatus(item, 'Archived')}
                            className="px-2 py-0.5 bg-zinc-950 text-slate-400 border border-zinc-800 rounded"
                          >
                            Archive
                          </button>
                        )}

                        <button
                          onClick={() => handleDeleteUpdate(item.id, item.title)}
                          className="p-0.5 text-slate-600 hover:text-rose-450"
                        >
                          <Trash2 size={10} />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* PUBLIC DISCLOSURE / LEGAL ADVISORY */}
      <div className="border border-zinc-900 bg-zinc-950/50 rounded-2xl p-4 font-mono text-[9px] leading-relaxed text-slate-500 uppercase select-none text-center">
        Technology updates are summarised from linked sources and may change quickly. Always verify details with the original source before making business, purchasing, or technical decisions. Summary based on linked source. Please verify details before making business decisions.
      </div>

      {/* SUBSCRIPTION MARKETING FLYER */}
      <div className="p-6 md:p-8 rounded-2xl border border-cyan-500/20 bg-zinc-950 relative overflow-hidden shadow-[0_0_30px_rgba(0,255,255,0.02)]">
        <div className="absolute inset-x-0 top-0 h-[1.5px] bg-gradient-to-r from-transparent via-[#00ffff]/40 to-transparent" />
        
        <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 relative">
          <div className="space-y-2 max-w-xl">
            <h3 className="font-sans font-black text-white text-md uppercase tracking-tight">Subscribe to Nexus Tech Advisory Reports</h3>
            <p className="text-[11px] text-slate-400 leading-relaxed font-sans font-medium">
              Join the weekly intelligence broadcast. Get pristine summaries regarding NexoraX Digital API modifications, Claude workflow modules, OpenAI developer changes, and tactile smart gadget announcements direct.
            </p>
          </div>

          <form onSubmit={handleSubscribe} className="space-y-3 shrink-0 lg:min-w-[400px]">
            <div className="flex gap-2">
              <input
                type="email"
                placeholder="Secure Email Routing Address"
                value={subscribedEmail}
                onChange={(e) => setSubscribedEmail(e.target.value)}
                className="w-full px-3 py-2 bg-black border border-zinc-850 rounded-xl font-mono text-[10px] text-white focus:outline-none focus:border-cyan-500"
                required
              />
              <button
                type="submit"
                className="py-2 px-5 bg-[#00ffff]/10 hover:bg-[#00ffff]/20 border border-[#00ffff]/40 rounded-xl font-mono text-[9px] font-black uppercase text-[#00ffff] tracking-widest cursor-pointer whitespace-nowrap"
              >
                Secure Access
              </button>
            </div>
            <label className="flex items-start gap-2 text-[8px] font-mono uppercase text-slate-500 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
                className="mt-0.5"
              />
              <span>I consent to weekly sweeping feeds based on linked sources. I verify details personally.</span>
            </label>

            {subMessage && (
              <div className="text-[9px] font-mono text-purple-400 font-extrabold bg-purple-950/25 px-3 py-1.5 rounded-xl border border-purple-900/25 mt-2">
                {subMessage}
              </div>
            )}
          </form>
        </div>
      </div>

      {/* DISPATCH TO AGENT INSTRUCTION MODAL */}
      {activeAgentUpdate && (
        <div className="fixed inset-0 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="w-full max-w-2xl rounded-2xl border border-purple-500/30 bg-zinc-950 overflow-hidden shadow-[0_0_50px_rgba(232,64,243,0.15)]"
          >
            <div className="p-6 border-b border-zinc-900/60 flex justify-between items-center bg-black/40">
              <div className="font-mono text-xs font-black uppercase tracking-widest text-[#e840f3]">
                Dispatching: Agent Dispatch Check
              </div>
              <button
                onClick={() => {
                  setActiveAgentUpdate(null);
                  setAgentResponse('');
                  setAgentInstruction('');
                }}
                className="p-1 px-3 bg-zinc-900 border border-zinc-800 hover:border-pink-500 text-slate-400 hover:text-white text-[10px] uppercase font-mono tracking-wider rounded"
              >
                Close
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="space-y-1">
                <h4 className="text-sm font-sans font-black uppercase text-white tracking-tight">
                  {activeAgentUpdate.title}
                </h4>
                <p className="text-[10px] font-mono text-slate-500">
                  Target URL: <span className="text-[#00ffff]">{activeAgentUpdate.sourceUrl}</span>
                </p>
              </div>

              <div className="space-y-2">
                <label className="block text-[8.5px] font-mono uppercase text-slate-400 font-extrabold">
                  Instruction Override Guidance (Optional)
                </label>
                <textarea
                  placeholder="e.g., Map client deployment guidelines, analyze B2B revenue margins..."
                  value={agentInstruction}
                  onChange={(e) => setAgentInstruction(e.target.value)}
                  rows={2}
                  className="w-full p-3 bg-black border border-zinc-900 rounded-xl font-mono text-[10.5px] text-white focus:outline-none focus:border-pink-500"
                />
              </div>

              {agentLoading && (
                <div className="py-8 text-center font-mono text-[10.5px] text-pink-400 uppercase flex flex-col items-center justify-center gap-2">
                  <RefreshCw className="h-5 w-5 animate-spin text-[#e840f3]" />
                  Establishing live tunnel link with NexoraX Antigravity core...
                </div>
              )}

              {agentResponse && (
                <div className="border border-zinc-900/80 bg-zinc-950 p-4 rounded-xl space-y-2 max-h-[300px] overflow-y-auto">
                  <div className="text-[7.5px] font-mono text-[#00ffff] uppercase font-black tracking-wider flex items-center gap-1 select-none">
                    <CheckCircle size={10} className="text-emerald-400" /> INTELLIGENCE BRIEF GENERATED SAFELY:
                  </div>
                  <pre className="text-[10px] font-mono text-slate-350 leading-relaxed whitespace-pre-wrap select-all">
                    {agentResponse}
                  </pre>
                </div>
              )}
            </div>

            <div className="p-6 bg-black/50 border-t border-zinc-900/60 flex justify-end gap-3 font-mono text-[10px] font-black uppercase tracking-wider">
              <button
                onClick={() => {
                  setActiveAgentUpdate(null);
                  setAgentResponse('');
                  setAgentInstruction('');
                }}
                className="py-3 px-5 bg-zinc-900 text-slate-400 border border-zinc-800 rounded-xl cursor-pointer"
              >
                Abort
              </button>
              <button
                onClick={dispatchAgentAnalysis}
                disabled={agentLoading}
                className="py-3 px-6 bg-gradient-to-r from-purple-900/40 to-purple-700/30 hover:to-[#e840f3]/25 border border-purple-500/35 rounded-xl text-[#e840f3] hover:text-white"
              >
                Execute Analysis
              </button>
            </div>
          </motion.div>
        </div>
      )}

      {/* ADMIN EDIT / ADD FEED DIALOG */}
      {isAdmin && isEditorOpen && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="w-full max-w-3xl rounded-2xl border border-pink-500/30 bg-zinc-950 overflow-hidden shadow-[0_0_50px_rgba(232,64,243,0.15)]"
          >
            <div className="p-5 border-b border-zinc-900 flex justify-between items-center bg-black/40">
              <div className="font-mono text-xs font-black uppercase tracking-widest text-[#e840f3]">
                {editingUpdate?.id ? 'Edit Radar parameters' : 'Publish new technology update'}
              </div>
              <button
                onClick={() => {
                  setIsEditorOpen(false);
                  setEditingUpdate(null);
                }}
                className="p-1 px-3 bg-zinc-900 border border-zinc-800 text-slate-400 hover:text-white text-[10px] uppercase font-mono rounded"
              >
                Abort
              </button>
            </div>

            <form onSubmit={handleSaveUpdateRecord} className="p-6 space-y-4 max-h-[500px] overflow-y-auto text-[10.5px]">
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="block text-[8px] font-mono text-slate-500 uppercase font-black">Update Title</label>
                  <input
                    type="text"
                    required
                    value={editingUpdate?.title || ''}
                    onChange={(e) => setEditingUpdate(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full p-2.5 bg-black border border-zinc-850 rounded-xl text-white"
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-[8px] font-mono text-slate-500 uppercase font-black">Source Website Name</label>
                  <input
                    type="text"
                    required
                    value={editingUpdate?.sourceName || ''}
                    onChange={(e) => setEditingUpdate(prev => ({ ...prev, sourceName: e.target.value }))}
                    className="w-full p-2.5 bg-black border border-zinc-850 rounded-xl text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="block text-[8px] font-mono text-slate-500 uppercase font-black">Source URL</label>
                  <input
                    type="text"
                    required
                    value={editingUpdate?.sourceUrl || ''}
                    onChange={(e) => setEditingUpdate(prev => ({ ...prev, sourceUrl: e.target.value }))}
                    className="w-full p-2.5 bg-black border border-zinc-850 rounded-xl text-white"
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-[8px] font-mono text-slate-500 uppercase font-black">Source Type</label>
                  <select
                    value={editingUpdate?.sourceType || 'Official blog'}
                    onChange={(e) => setEditingUpdate(prev => ({ ...prev, sourceType: e.target.value }))}
                    className="w-full p-2.5 bg-black border border-zinc-850 rounded-xl text-white uppercase font-bold"
                  >
                    {SOURCE_TYPES.map(st => (
                      <option key={st} value={st}>{st}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1">
                  <label className="block text-[8px] font-mono text-slate-500 uppercase font-black">Category</label>
                  <select
                    value={editingUpdate?.category || 'AI Models'}
                    onChange={(e) => setEditingUpdate(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full p-2.5 bg-black border border-zinc-850 rounded-xl text-white"
                  >
                    {UPDATE_CATEGORIES.slice(1).map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="block text-[8px] font-mono text-slate-500 uppercase font-black">Provider Hub</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. OpenAI / ChatGPT, Gemini, Anthropic"
                    value={editingUpdate?.provider || ''}
                    onChange={(e) => setEditingUpdate(prev => ({ ...prev, provider: e.target.value }))}
                    className="w-full p-2.5 bg-black border border-zinc-850 rounded-xl text-white"
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-[8px] font-mono text-slate-500 uppercase font-black">Impact Rating</label>
                  <select
                    value={editingUpdate?.impactLevel || 'Useful'}
                    onChange={(e) => setEditingUpdate(prev => ({ ...prev, impactLevel: e.target.value }))}
                    className="w-full p-2.5 bg-black border border-zinc-850 rounded-xl text-white font-bold"
                  >
                    {IMPACT_LEVEL_CATEGORIES.slice(1).map(imp => (
                      <option key={imp} value={imp}>{imp}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-1">
                <label className="block text-[8px] font-mono text-slate-500 uppercase font-black">Technical summary</label>
                <textarea
                  required
                  rows={3}
                  value={editingUpdate?.summary || ''}
                  onChange={(e) => setEditingUpdate(prev => ({ ...prev, summary: e.target.value }))}
                  className="w-full p-2.5 bg-black border border-zinc-850 rounded-xl text-slate-200"
                />
              </div>

              <div className="space-y-1">
                <label className="block text-[8px] font-mono text-slate-500 uppercase font-black">Relevance for NexoraX Client Accounts</label>
                <textarea
                  placeholder="Analyze local business advantages, 0.8s SLA boosts..."
                  rows={2}
                  value={editingUpdate?.relevanceForNexoraX || ''}
                  onChange={(e) => setEditingUpdate(prev => ({ ...prev, relevanceForNexoraX: e.target.value }))}
                  className="w-full p-2.5 bg-black border border-zinc-850 rounded-xl text-slate-200"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="block text-[8px] font-mono text-slate-500 uppercase font-black">Release / Detected Date</label>
                  <input
                    type="date"
                    value={editingUpdate?.releaseDate || new Date().toISOString().split('T')[0]}
                    onChange={(e) => setEditingUpdate(prev => ({ ...prev, releaseDate: e.target.value, detectedDate: e.target.value }))}
                    className="w-full p-2.5 bg-black border border-zinc-850 rounded-xl text-white"
                  />
                </div>

                <div className="space-y-1">
                  <label className="block text-[8px] font-mono text-slate-500 uppercase font-black">Tags (comma separated)</label>
                  <input
                    type="text"
                    placeholder="AI, API, ChatGPT"
                    value={editingUpdate?.tags?.join(', ') || ''}
                    onChange={(e) => setEditingUpdate(prev => ({ ...prev, tags: e.target.value.split(',').map(s => s.trim()).filter(Boolean) }))}
                    className="w-full p-2.5 bg-black border border-zinc-850 rounded-xl text-white"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
                <div className="space-y-1">
                  <label className="block text-[8px] font-mono text-slate-500 uppercase font-black">Document Status State</label>
                  <select
                    value={editingUpdate?.status || 'Published'}
                    onChange={(e) => setEditingUpdate(prev => ({ ...prev, status: e.target.value as any }))}
                    className="w-full p-2.5 bg-black border border-zinc-850 rounded-xl text-white uppercase font-bold"
                  >
                    <option value="Draft">Draft</option>
                    <option value="Published">Published (Public visible)</option>
                    <option value="Needs Review">Needs Review</option>
                    <option value="Watchlist">Watchlist</option>
                    <option value="Archived">Archived</option>
                  </select>
                </div>

                <div className="space-y-1">
                  <label className="block text-[8px] font-mono text-slate-500 uppercase font-black">Manual Review Verification Label</label>
                  <select
                    value={editingUpdate?.manualReviewStatus || 'Reviewed'}
                    onChange={(e) => setEditingUpdate(prev => ({ ...prev, manualReviewStatus: e.target.value as any }))}
                    className="w-full p-2.5 bg-black border border-zinc-850 rounded-xl text-white uppercase font-bold"
                  >
                    <option value="Unreviewed">Unreviewed</option>
                    <option value="Reviewed">Reviewed</option>
                    <option value="Verified Source">Verified Source</option>
                    <option value="Official Source">Official Source</option>
                    <option value="Needs Fact Check">Needs Fact Check</option>
                    <option value="Do Not Publish">Do Not Publish</option>
                  </select>
                </div>
              </div>

              <div className="p-4 bg-black/50 border border-zinc-900 rounded-xl flex justify-end gap-3 font-mono text-[10px] font-black uppercase tracking-wider">
                <button
                  type="button"
                  onClick={() => {
                    setIsEditorOpen(false);
                    setEditingUpdate(null);
                  }}
                  className="py-2.5 px-4 bg-zinc-900 text-slate-400 border border-zinc-800 rounded-xl"
                >
                  Close
                </button>
                <button
                  type="submit"
                  className="py-2.5 px-5 bg-gradient-to-r from-purple-900/40 to-purple-700/30 hover:to-[#e840f3]/25 border border-purple-500/35 rounded-xl text-white"
                >
                  Save Record
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      {/* WATCHLIST CHANNEL MODAL EDITOR */}
      {isAdmin && isSourceFormOpen && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 z-50">
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="w-full max-w-lg rounded-2xl border border-[#00ffff]/30 bg-zinc-950 overflow-hidden shadow-[0_0_50px_rgba(0,255,255,0.1)]"
          >
            <div className="p-5 border-b border-zinc-900 flex justify-between items-center bg-black/40">
              <div className="font-mono text-xs font-black uppercase tracking-widest text-[#00ffff]">
                Configure source watchlist channel
              </div>
              <button
                onClick={() => {
                  setIsSourceFormOpen(false);
                  setEditingSource(null);
                }}
                className="p-1 px-3 bg-zinc-900 border border-zinc-800 text-slate-400 hover:text-white text-[10px] uppercase font-mono rounded"
              >
                Abort
              </button>
            </div>

            <form onSubmit={handleSaveSource} className="p-6 space-y-4 text-[10.5px]">
              <div className="space-y-1">
                <label className="block text-[8px] font-mono text-slate-500 uppercase font-black">Channel Name</label>
                <input
                  type="text"
                  required
                  placeholder="Anthropic Newsroom"
                  value={editingSource?.name || ''}
                  onChange={(e) => setEditingSource(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full p-2.5 bg-black border border-zinc-850 rounded-xl text-white"
                />
              </div>

              <div className="space-y-1">
                <label className="block text-[8px] font-mono text-slate-500 uppercase font-black">Channel changelog URL</label>
                <input
                  type="text"
                  required
                  placeholder="https://..."
                  value={editingSource?.url || ''}
                  onChange={(e) => setEditingSource(prev => ({ ...prev, url: e.target.value }))}
                  className="w-full p-2.5 bg-black border border-zinc-850 rounded-xl text-white"
                />
              </div>

              <div className="space-y-1">
                <label className="block text-[8px] font-mono text-slate-500 uppercase font-black">Group Category</label>
                <select
                  value={editingSource?.category || 'Official AI Sources'}
                  onChange={(e) => setEditingSource(prev => ({ ...prev, category: e.target.value as any }))}
                  className="w-full p-2.5 bg-black border border-zinc-850 rounded-xl text-white"
                >
                  <option value="Official AI Sources">Official AI Sources</option>
                  <option value="Automation Sources">Automation Sources</option>
                  <option value="Developer Sources">Developer Sources</option>
                  <option value="Smart Tech Sources">Smart Tech Sources</option>
                </select>
              </div>

              <div className="space-y-1">
                <label className="block text-[8px] font-mono text-slate-500 uppercase font-black">API connection / tracking status</label>
                <select
                  value={editingSource?.status || 'Manual tracking'}
                  onChange={(e) => setEditingSource(prev => ({ ...prev, status: e.target.value as any }))}
                  className="w-full p-2.5 bg-black border border-zinc-850 rounded-xl text-white uppercase font-bold"
                >
                  <option value="Manual tracking">Manual tracking</option>
                  <option value="Official feed connected">Official feed connected</option>
                  <option value="API connected server-side">API connected server-side</option>
                  <option value="Needs review">Needs review</option>
                  <option value="Disabled">Disabled</option>
                </select>
              </div>

              <div className="p-4 bg-black/50 border border-zinc-900 rounded-xl flex justify-end gap-3 font-mono text-[10px] font-black uppercase tracking-wider">
                <button
                  type="button"
                  onClick={() => {
                    setIsSourceFormOpen(false);
                    setEditingSource(null);
                  }}
                  className="py-2.5 px-4 bg-zinc-900 text-slate-400 border border-zinc-800 rounded-xl"
                >
                  Close
                </button>
                <button
                  type="submit"
                  className="py-2.5 px-5 bg-gradient-to-r from-cyan-950/40 to-cyan-700/30 hover:to-[#00ffff]/25 border border-cyan-500/35 rounded-xl text-white"
                >
                  Deploy Channel
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

    </div>
  );
};
